/** Bullhorn entity types included in the unified sync. */
export type BullhornSyncEntityType =
  | 'crm_company'
  | 'crm_contact'
  | 'candidate'
  | 'vacancy'
  | 'placement'
  | 'pipeline'
  | 'pipeline_history';

export const BULLHORN_SYNC_ENTITY_ORDER: BullhornSyncEntityType[] = [
  'crm_company',
  'crm_contact',
  'vacancy',
  'candidate',
  'placement',
  'pipeline',
  'pipeline_history',
];

export const BULLHORN_SYNC_ENTITY_LABELS: Record<BullhornSyncEntityType, string> = {
  crm_company: 'CRM — Bedrijven',
  crm_contact: 'CRM — Contacten',
  candidate: 'Kandidaten',
  vacancy: 'Vacatures',
  placement: 'Plaatsingen',
  pipeline: 'Pipelines (sollicitaties)',
  pipeline_history: 'Pipeline-geschiedenis',
};

export interface BullhornSyncState {
  initialFullSyncCompleted?: boolean;
  /** Global watermark for incremental sync (ISO). */
  lastSyncAt?: string;
  entityLastSyncAt?: Partial<Record<BullhornSyncEntityType, string>>;
}

/** Epoch ms for Bullhorn Lucene range queries. */
export function syncSinceMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Lucene (search/Candidate, search/JobOrder). */
export function bhLuceneQuery(base: string, sinceMs: number | null): string {
  if (sinceMs == null) return base;
  return `${base} AND dateLastModified:[${sinceMs} TO *]`;
}

/** SQL where (query/ClientCorporation, query/ClientContact, query/Placement). */
export function bhSqlWhere(base: string, sinceMs: number | null): string {
  if (sinceMs == null) return base;
  return `${base} AND dateLastModified>${sinceMs}`;
}

export function recordDisplayName(entity: BullhornSyncEntityType, row: Record<string, unknown>): string {
  switch (entity) {
    case 'crm_company':
      return String(row.name || `Bedrijf #${row.id}`);
    case 'crm_contact':
      return [row.firstName, row.lastName].filter(Boolean).join(' ') || String(row.email || `Contact #${row.id}`);
    case 'candidate':
      return [row.firstName, row.lastName].filter(Boolean).join(' ') || String(row.email || `Kandidaat #${row.id}`);
    case 'vacancy':
      return String(row.title || `Vacature #${row.id}`);
    case 'placement':
      return `Plaatsing #${row.id}`;
    case 'pipeline':
    case 'pipeline_history':
      return `Submission #${row.id}`;
    default:
      return `#${row.id}`;
  }
}

export function bhDateLastModified(row: Record<string, unknown>): number | null {
  const raw = row.dateLastModified ?? row.dateAdded;
  if (raw == null) return null;
  if (typeof raw === 'number') return raw > 0 && raw < 1e12 ? raw * 1000 : raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    const n = Number(raw);
    return n > 0 && n < 1e12 ? n * 1000 : n;
  }
  const t = Date.parse(String(raw));
  return Number.isNaN(t) ? null : t;
}
